import { useState, useEffect, useCallback } from 'react';
import AvailabilityCalendar from './AvailabilityCalendar';
import { useAvailability } from '../hooks/useAvailability';

const pad = (n) => String(n).padStart(2, '0');

/**
 * AvailabilityManager — interactive calendar for artists to manage their availability.
 * Wraps AvailabilityCalendar in interactive mode + CRUD controls.
 */
export default function AvailabilityManager() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());

  // Selection state — explicit phase machine:
  //   'idle'          → nothing selected, waiting for first click
  //   'start'         → one day selected, waiting for optional second click to extend range
  //   'rangeComplete' → two days selected, range is locked
  const [phase, setPhase] = useState('idle');
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);

  // Form state
  const [formStatus, setFormStatus] = useState('available');
  const [formNotes, setFormNotes] = useState('');
  const [editingSlot, setEditingSlot] = useState(null);

  const {
    slots,
    loading,
    error,
    fetchMySlots,
    createSlot,
    updateSlot,
    deleteSlot,
    setError,
  } = useAvailability();

  // Compute date range for current month view (include a buffer for display)
  const getMonthRange = useCallback((m, y) => {
    const from = `${y}-${pad(m + 1)}-01`;
    const lastDay = new Date(y, m + 1, 0).getDate();
    const to = `${y}-${pad(m + 1)}-${pad(lastDay)}`;
    return { from, to };
  }, []);

  // Fetch slots when month changes
  useEffect(() => {
    const { from, to } = getMonthRange(month, year);
    fetchMySlots(from, to);
  }, [month, year, fetchMySlots, getMonthRange]);

  function handleMonthChange(newMonth, newYear) {
    setMonth(newMonth);
    setYear(newYear);
  }

  function handleDayClick(dateStr) {
    // If editing a slot, ignore day clicks
    if (editingSlot) return;

    if (phase === 'idle') {
      // First click: select a single day
      setSelectionStart(dateStr);
      setSelectionEnd(null);
      setPhase('start');
    } else if (phase === 'start') {
      // Second click: same day = deselect, different day = complete range
      if (dateStr === selectionStart) {
        clearSelection();
        return;
      }
      // Order the dates correctly
      if (dateStr < selectionStart) {
        setSelectionEnd(selectionStart);
        setSelectionStart(dateStr);
      } else {
        setSelectionEnd(dateStr);
      }
      setPhase('rangeComplete');
    } else {
      // rangeComplete: clicking again resets and starts fresh
      // But if same day as start or end → just deselect
      if (dateStr === selectionStart || dateStr === selectionEnd) {
        clearSelection();
        return;
      }
      setSelectionStart(dateStr);
      setSelectionEnd(null);
      setPhase('start');
    }
  }

  function clearSelection() {
    setSelectionStart(null);
    setSelectionEnd(null);
    setPhase('idle');
    setFormStatus('available');
    setFormNotes('');
    setEditingSlot(null);
    setError(null);
  }

  async function handleCreateSlot() {
    if (!selectionStart) return;
    const endDate = selectionEnd || selectionStart;
    try {
      await createSlot({
        startDate: selectionStart,
        endDate,
        status: formStatus,
        notes: formNotes || undefined,
      });
      clearSelection();
      // Refresh
      const { from, to } = getMonthRange(month, year);
      await fetchMySlots(from, to);
    } catch {
      // Error already set by hook
    }
  }

  async function handleUpdateSlot() {
    if (!editingSlot) return;
    try {
      await updateSlot(editingSlot.id, {
        startDate: selectionStart || undefined,
        endDate: selectionEnd || selectionStart || undefined,
        status: formStatus,
        notes: formNotes || undefined,
      });
      clearSelection();
      const { from, to } = getMonthRange(month, year);
      await fetchMySlots(from, to);
    } catch {
      // Error already set by hook
    }
  }

  async function handleDeleteSlot(slotId) {
    try {
      await deleteSlot(slotId);
      clearSelection();
      const { from, to } = getMonthRange(month, year);
      await fetchMySlots(from, to);
    } catch {
      // Error already set by hook
    }
  }

  function handleSlotClick(slot) {
    // Enter edit mode for this slot
    setEditingSlot(slot);
    setSelectionStart(slot.startDate?.split('T')[0] || slot.startDate);
    setSelectionEnd(slot.endDate?.split('T')[0] || slot.endDate);
    setPhase('rangeComplete');
    setFormStatus(slot.status);
    setFormNotes(slot.notes || '');
  }

  const selectedRange = selectionStart
    ? { start: selectionStart, end: selectionEnd || selectionStart }
    : null;

  const hasSelection = selectionStart !== null;
  const isBooked = editingSlot && (editingSlot.status === 'booked' || editingSlot.status === 'pending');

  return (
    <div className="space-y-4">
      <AvailabilityCalendar
        slots={slots}
        month={month}
        year={year}
        onMonthChange={handleMonthChange}
        mode="interactive"
        onDayClick={handleDayClick}
        onSlotClick={handleSlotClick}
        selectedRange={selectedRange}
      />

      {/* Error display */}
      {error && (
        <div className="border border-status-rejected/30 bg-status-rejected/10 p-3 text-xs font-mono text-status-rejected">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Fermer
          </button>
        </div>
      )}

      {/* Action panel */}
      {hasSelection && (
        <div className="border th-border th-bg-surface p-4 space-y-3 animate-fadeIn">
          <div className="flex items-center justify-between">
            <p className="text-xs font-mono th-text-muted uppercase tracking-widest">
              {editingSlot ? 'Modifier le créneau' : 'Nouveau créneau'}
            </p>
            <button
              onClick={clearSelection}
              className="text-xs font-mono th-text-muted hover:th-text transition-colors"
            >
              Annuler
            </button>
          </div>

          {/* Date display */}
          <div className="text-sm font-mono th-text">
            {selectionStart === (selectionEnd || selectionStart)
              ? selectionStart
              : `${selectionStart} → ${selectionEnd || selectionStart}`}
          </div>

          {/* Status toggle */}
          {!isBooked && (
            <div className="flex gap-2">
              <button
                onClick={() => setFormStatus('available')}
                className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 border transition-colors duration-150 ${
                  formStatus === 'available'
                    ? 'border-accent bg-accent/20 text-accent'
                    : 'th-border th-text-muted hover:th-text'
                }`}
              >
                Disponible
              </button>
              <button
                onClick={() => setFormStatus('blocked')}
                className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 border transition-colors duration-150 ${
                  formStatus === 'blocked'
                    ? 'border-status-cancelled bg-status-cancelled/20 text-status-cancelled'
                    : 'th-border th-text-muted hover:th-text'
                }`}
              >
                Bloqué
              </button>
            </div>
          )}

          {/* Notes */}
          {!isBooked && (
            <input
              type="text"
              value={formNotes}
              onChange={(e) => setFormNotes(e.target.value)}
              placeholder="Notes (optionnel)"
              className="th-input w-full text-sm"
            />
          )}

          {/* Action buttons */}
          <div className="flex gap-2">
            {editingSlot ? (
              <>
                {!isBooked && (
                  <button
                    onClick={handleUpdateSlot}
                    disabled={loading}
                    className="th-btn-accent flex-1 disabled:opacity-50"
                  >
                    {loading ? 'Sauvegarde...' : 'Sauvegarder'}
                  </button>
                )}
                {!isBooked && (
                  <button
                    onClick={() => handleDeleteSlot(editingSlot.id)}
                    disabled={loading}
                    className="border border-status-rejected/30 text-status-rejected text-xs font-mono uppercase tracking-widest px-3 py-1.5 hover:bg-status-rejected/10 transition-colors duration-150 disabled:opacity-50"
                  >
                    Supprimer
                  </button>
                )}
                {isBooked && (
                  <p className="text-xs font-mono th-text-muted italic">
                    Ce créneau est lié à un booking, non modifiable.
                  </p>
                )}
              </>
            ) : (
              <button
                onClick={handleCreateSlot}
                disabled={loading}
                className="th-btn-accent flex-1 disabled:opacity-50"
              >
                {loading ? 'Création...' : 'Créer le créneau'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Contextual help text */}
      {!loading && (
        <p className="text-xs font-mono th-text-muted text-center">
          {phase === 'idle' && 'Cliquez sur un jour pour commencer, ou sur un créneau existant pour le modifier.'}
          {phase === 'start' && !editingSlot && 'Cliquez un 2e jour pour étendre la sélection, ou re-cliquez le même jour pour annuler.'}
          {phase === 'rangeComplete' && !editingSlot && 'Sélection prête, configurez le créneau ci-dessus.'}
        </p>
      )}
    </div>
  );
}
